import type { MemberSearchResult } from '@yawi/domain';
import type { HikCentralImportPreviewRow } from '@yawi/types';
import { AlertTriangle } from 'lucide-react';
import { MemberSearchCell } from './MemberSearchCell';

interface Assignment {
  memberId: string;
  displayName: string;
}

interface HikCentralPreviewTableProps {
  rows: HikCentralImportPreviewRow[];
  assignments: Record<number, Assignment>;
  onAssignmentChange: (rowIndex: number, memberId: string, displayName: string) => void;
  onSearch: (query: string) => Promise<MemberSearchResult[]>;
  disabled?: boolean;
}

export function HikCentralPreviewTable({ rows, assignments, onAssignmentChange, onSearch, disabled }: HikCentralPreviewTableProps) {
  const assignedCount = Object.values(assignments).filter((a) => a.memberId).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-theme-secondary">
          {rows.length} filas · <span className="font-medium text-indigo-600">{assignedCount} asignadas</span>
        </p>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-theme-secondary uppercase tracking-wide">#</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-theme-secondary uppercase tracking-wide">Código</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-theme-secondary uppercase tracking-wide">Nombre (HikCentral)</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-theme-secondary uppercase tracking-wide min-w-[240px]">Asignar a miembro</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-theme-secondary uppercase tracking-wide">Tarjetas</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-theme-secondary uppercase tracking-wide">Alertas</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {rows.map((row) => {
              const assignment = assignments[row.rowIndex];
              const currentMemberId = assignment?.memberId ?? '';
              const currentLabel = assignment?.displayName ?? '';
              const hasWarnings = row.warnings.length > 0;

              return (
                <tr
                  key={row.rowIndex}
                  className={`transition-colors ${
                    hasWarnings
                      ? 'bg-amber-50/50 dark:bg-amber-900/10'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800/30'
                  }`}
                >
                  <td className="px-4 py-3 text-theme-secondary">{row.rowIndex + 1}</td>
                  <td className="px-4 py-3 font-mono text-xs text-theme">{row.personCode}</td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-theme">
                      {row.fullName || <span className="italic text-slate-400">Sin nombre</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <MemberSearchCell
                      row={row}
                      value={currentMemberId}
                      valueLabel={currentLabel}
                      onChange={(memberId, displayName) => onAssignmentChange(row.rowIndex, memberId, displayName)}
                      onSearch={onSearch}
                      disabled={disabled}
                    />
                  </td>
                  <td className="px-4 py-3">
                    {row.cards.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {row.cards.map((card) => (
                          <span key={card} className="px-1.5 py-0.5 rounded text-xs font-mono bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400">
                            {card}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {hasWarnings ? (
                      <div className="flex flex-col gap-0.5">
                        {row.warnings.map((w, i) => (
                          <span key={i} className="flex items-center gap-1 text-xs text-amber-700">
                            <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                            {w}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
